'use client';

import dynamic from 'next/dynamic';
import { ReactNode, useEffect, useState } from 'react';
import { ListingBannerItem } from '@/features/CMS/banners/types';
import { injectListingBanners } from '@/features/products/components/list/banners/listingPromoBanner/utils';
import { ProductListPagination } from '@/features/products/components/list/productListPagination/productListPagination';
import ProductListBox from '@/features/products/components/list/productListSection/productListBox';
import { ProductListLengthInfo } from '@/features/products/components/list/productListSection/productListLengthInfo';
import { ProductListRowItem } from '@/features/products/components/list/productListSection/types';
import {
  useProductListItems,
  useProductListPage,
  useProductListPages,
  useProductListTotalItems
} from '@/features/products/state/list/context';
import { ProductListingItem } from '@/features/products/types/product';
import { ListDTO } from '@/types/dto/list.dto';

const PosthogListSearchQueryTracker = dynamic(
  () => import('@/events/Posthog/posthogListSearchQueryTracker').then((mod) => mod),
  { ssr: false }
);

export interface ProductListSectionProps {
  isPartnerListing?: boolean;
  products: ListDTO<ProductListingItem> | null;
  searchParamsList: Record<string, string>;
  currentPage?: number;
  listingBanners: ListingBannerItem[];
  sortingButton: ReactNode;
}

type ClientCurrentPageData = Pick<
  ListDTO<ProductListingItem>,
  'totalItems' | 'pages' | 'items'
> | null;

const prepareItemsToRender = (
  items: ProductListingItem[] | undefined,
  listingBanners: ListingBannerItem[],
  page: number,
  isPartnerListing?: boolean
): ProductListRowItem[] => {
  if (typeof items === 'undefined' || !items.length) {
    return [];
  }

  if (isPartnerListing) {
    return items;
  }

  return injectListingBanners(items, listingBanners, page);
};

export const ProductListSection = ({
  isPartnerListing,
  products,
  searchParamsList,
  currentPage,
  listingBanners,
  sortingButton
}: ProductListSectionProps) => {
  const itemsToRender = prepareItemsToRender(
    products?.items,
    listingBanners,
    currentPage || 1,
    isPartnerListing
  );
  const pages = useProductListPages();
  const items = useProductListItems();
  const page = useProductListPage();
  const totalItems = useProductListTotalItems();
  const [clientItemsToRender, setClientItemsToRender] = useState<ClientCurrentPageData>(null);

  useEffect(() => {
    // ? Switch from the SSR-provided `products` snapshot to the live reducer state as
    // ? soon as it actually diverges from it (a client-side filter/sort/page dispatch
    // ? happened) — compared directly against the original prop, not gated on whether
    // ? the URL had any query param at the initial page load (that check meant a filter
    // ? change on a plain, param-less first load never refreshed the displayed count).
    const productsItemsIds = products?.items.map((item) => item.id) || [];
    const itemsIds = items.map((item) => item.id);
    const hasDivergedFromSsrSnapshot =
      (products?.totalItems || 0) !== totalItems ||
      JSON.stringify(productsItemsIds) !== JSON.stringify(itemsIds);

    if (hasDivergedFromSsrSnapshot) {
      setClientItemsToRender({
        pages,
        items: prepareItemsToRender(
          items,
          listingBanners,
          page,
          isPartnerListing
        ) as ProductListingItem[],
        totalItems
      });
    } else {
      setClientItemsToRender(null);
    }
  }, [items, totalItems, currentPage, page, listingBanners]);

  const itemsToRenderCount =
    typeof clientItemsToRender?.totalItems !== 'undefined'
      ? clientItemsToRender?.totalItems
      : products?.totalItems || 0;

  return (
    <>
      <div className="flex justify-between lg:mb-6">
        <ProductListLengthInfo totalItems={itemsToRenderCount} />
        {sortingButton}
      </div>
      <div className="flex flex-col items-center gap-6 w-full grow content-[auto] [contain-intrinsic-size:auto_4500px] px-4 lg:px-0">
        <ProductListBox
          isPartnerListing={!!isPartnerListing}
          items={
            typeof clientItemsToRender?.items !== 'undefined'
              ? clientItemsToRender?.items
              : itemsToRender
          }
        />
        <ProductListPagination
          totalPages={
            typeof clientItemsToRender?.pages !== 'undefined'
              ? clientItemsToRender?.pages
              : products?.pages || 1
          }
        />

        <PosthogListSearchQueryTracker query={searchParamsList['query']} />
      </div>
    </>
  );
};
